import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { Roi } from '../patient/roi';
import { StructureSet } from '../patient/structureset';
import {
    ApprovalView,
    AvailableStructureSetView,
    RoiDetailView,
    RoiListItemView,
    StructureSetDetailView,
    StructureSetListItemView,
} from './views';

export function createRoiDetailView(roi: Roi): RoiDetailView {
    return {
        id: roi.id,
        roiNumber: roi.roiNumber,
        name: roi.name,
        color: { ...roi.color },
        approvalStatus: roi.approvalStatus,
        interpretedType: roi.interpretedType,
        category: roi.category,
        volumeCm3: roi.volumeMm3 != null ? roi.volumeMm3 / 1000 : null,
        contourCount: roi.contourCount,
        isEditable: !roi.isApproved,
        isEmpty: roi.isEmpty,
        isPointRoi: roi.isPointRoi,
    };
}

export function createRoiListItemView(roi: Roi): RoiListItemView {
    return {
        id: roi.id,
        roiNumber: roi.roiNumber,
        name: roi.name,
        category: roi.category,
        color: { ...roi.color },
        isApproved: roi.isApproved,
        isEmpty: roi.isEmpty,
    };
}

export function createStructureSetListItemView(
    ss: StructureSet
): StructureSetListItemView {
    return {
        id: ss.id,
        dicomUid: ss.dicomUid,
        displayName: ss.structureSetLabel,
        approvalStatus: ss.approvalStatus,
        roiCount: ss.rois.length,
        syncStatus: ss.syncStatus,
    };
}

export function createApprovalView(ss: StructureSet): ApprovalView {
    return {
        structureSetId: ss.id,
        approvalStatus: ss.approvalStatus,
        reviewerName: ss.reviewerName || null,
        canApprove: ss.approvalStatus !== DICOMApprovalStatus.APPROVED,
        canReject: ss.approvalStatus !== DICOMApprovalStatus.REJECTED,
        cascadingStatus: ss.getCascadingApprovalStatus(),
    };
}

/**
 * Detail view for the structure-set header and properties panel.
 * Denormalises image references so the UI does not need to navigate
 * ss.image directly.
 */
export function createStructureSetDetailView(
    ss: StructureSet
): StructureSetDetailView {
    const image = ss.image;
    return {
        id: ss.id,
        dicomUid: ss.dicomUid,
        displayName: ss.structureSetLabel,
        description: ss.structureSetDescription,
        approvalStatus: ss.approvalStatus,
        isApproved: ss.isApproved,
        reviewerName: ss.reviewerName,
        reviewDate: ss.reviewDate,
        reviewTime: ss.reviewTime,
        frameOfReferenceUid: ss.frameOfReferenceUid,
        numberOfStructures: ss.numberOfStructures,
        manufacturer: ss.manufacturer,
        manufacturerModelName: ss.manufacturerModelName,
        softwareVersions: ss.softwareVersions,
        generationModel: ss.generationModel,
        imageUid: image?.dicomUid ?? null,
        imageLabel: image?.seriesMetadata.seriesDescription ?? null,
        imageSeriesUid: image?.seriesUid ?? null,
    };
}

/**
 * AvailableStructureSetView factory for a structure set already
 * loaded into the patient aggregate.
 */
export function createAvailableStructureSetViewFromDomain(
    ss: StructureSet
): AvailableStructureSetView {
    return {
        sopInstanceUid: ss.dicomUid,
        displayName: ss.structureSetLabel,
        isLoaded: true,
        imageLabel: ss.image?.seriesMetadata.seriesDescription || undefined,
        imageSeriesUid: ss.image?.seriesUid || undefined,
        structureSetId: ss.id,
    };
}

/**
 * AvailableStructureSetView factory for a session-discoverable
 * structure set that has not yet been loaded. The display name and
 * SOP UID are taken from the session payload — no domain id exists
 * because there is no domain object yet.
 */
export function createAvailableStructureSetViewFromSession(args: {
    sopInstanceUid: string;
    displayName: string;
}): AvailableStructureSetView {
    return {
        sopInstanceUid: args.sopInstanceUid,
        displayName: args.displayName,
        isLoaded: false,
    };
}
